import { IEngineRequest } from '@df/catalog-ui/related-products/related-products.service';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { IProductDataV2, Product } from '@df/catalog/product/product';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            klevu: KlevuSearch.IConfig;
        }
    }
}

declare global {
    interface Window {
        klevu_urlProtocol?: string;
        klevu_javascriptDomain?: string;
        klevu_webstorePopularTerms?: string[];
        klevu_popularProductsOfSite?: KlevuSearch.IPopularProduct[];
        klevu_keywordUrlMap?: KlevuSearch.IKeywordUrlMap[];
        klevu_uc_userOptions?: KlevuSearch.InoResultMessage;
        klevu_banner?: KlevuSearch.IBanner[];
    }
}

export namespace KlevuSearch {
    export enum ETypeOfRequest {
        Search = 'SEARCH',
        NewArrivals = 'RECS_NEW_ARRIVALS',
        Similar = 'RECS_SIMILAR',
        Trending = 'RECS_TRENDING',
        AlsoViewed = 'RECS_ALSO_VIEWED',
        AlsoBought = 'RECS_ALSO_BOUGHT',
        VisuallySimilar = 'RECS_SIMILAR_IMAGES'
    }

    export enum ANALYTICS_ENDPOINTS {
        search = 'https://stats.klevu.com/analytics/n-search/search',
        searchProduct = 'https://stats.klevu.com/analytics/productTracking',
        categoryProductView = 'https://stats.ksearchnet.com/analytics/categoryProductViewTracking',
        categoryProductClick = 'https://stats.ksearchnet.com/analytics/categoryProductClickTracking',
        recommendationsCollect = 'https://stats.ksearchnet.com/analytics/collect',
        bannerTrack = 'https://stats.ksearchnet.com/analytics/dataTracking'
    }

    export enum ETypeOfRecord {
        Product = 'KLEVU_PRODUCT'
    }

    export const catNavSearchType = 'CAT_NAV';

    /**
     * Module configuration
     */
    export interface IConfig {
        /**
         * Search endpoint
         * so in this:
         * http://<cloudSearchHostURL>/cloud-search/n-search/search
         * its this part:
         * http://<cloudSearchHostURL>
         *
         */
        endpoint: string;
        /**
         * See ISuggestionRequest.ticket
         */
        apiKey: string;
        /**
         * See ISuggestionRequest.sv
         */
        magentoModuleVersion?: string;
    }

    /**
     * Search request
     * https://developers.klevu.com/api/#json-structure
     */
    export interface ISearchRequest {
        context: IContext;
        suggestions?: ISuggestionRequestItem[];
        recordQueries?: ISearchRequestItem[];
    }

    /**
     * Request context data
     */
    export interface IContext {
        /**
         * API keys
         */
        apiKeys: string[];
    }

    /**
     * Suggestion search results
     * https://www.klevu.com/developer-api/api/autosuggestion-search.html
     */
    export interface ISuggestionRequestItem {
        /**
         * 	Every suggestion request must have a unique identifier associated with it.
         *  When a result is retrieved, the ID of each query is associated with it's respective response.
         */
        id: string;
        /**
         * The type of suggestion query. Specify 'AUTO_SUGGESTIONS' for typeahead autosuggestion predictions.
         */
        typeOfQuery: 'AUTO_SUGGESTIONS';
        /**
         * The search term or phrase for which the suggestions are retrieved from Klevu Search.
         */
        query: string;
        /**
         * The maximum number of suggestions to be retrieved
         */
        limit: number;

        /**
         * By default, the section of an autosuggestion matching what the customer has typed is highlighted
         * in bold using values of '<b>' and </b>'.
         * If you prefer something else, you can override these values with your own HTML.
         */
        hlStartElem?: string;
        /**
         * By default, the section of an autosuggestion matching what the customer has typed is highlighted
         * in bold using values of '<b>' and </b>'.
         * If you prefer something else, you can override these values with your own HTML.
         */
        hlEndElem?: string;
    }

    /**
     * Search request
     */
    export interface ISearchRequestItem {
        /**
         * 	Every suggestion request must have a unique identifier associated with it.
         *  When a result is retrieved, the ID of each query is associated with it's respective response.
         */
        id: string;
        /**
         * This parameter defines the type of request it is, which should be 'SEARCH' for search queries.
         * There are other values available, detailed elsewhere in the documentation.
         */
        typeOfRequest: ETypeOfRequest.Search;

        settings: {
            query: {
                /**
                 * This is the phrase to be searched. It can be any free text up-to 128 characters long.
                 * If a longer string is provided it is automatically truncated after the first 128 characters.
                 */
                term: string;
            };
            /**
             * The maximum number of records to retrieve.
             */
            limit: number;
            /**
             * Here you can specify the types of records to be retrieved, for example KLEVU_PRODUCT, KLEVU_CMS, KLEVU_CATEGORY, etc.
             * In the case where more than one type of record is specified, the results fetched may be a mixture of all
             * the different types of records specified. The maximum number of results retrieved will never be more than the limit
             * specified. Should you wish to retrieve records of each type separately, for example 5 Products + 5 Categories,
             * a separate query must be submitted for each data type required.
             */
            typeOfRecords: TRecord[];
            /**
             * Fields to pull
             */
            fields: string[];
            /**
             * Sort order
             * https://developers.klevu.com/api/#sorting
             */
            sort?:
                | 'RELEVANCE' // default
                | 'PRICE_ASC'
                | 'PRICE_DESC'
                | 'NAME_ASC'
                | 'NAME_DESC'
                | 'RATING_ASC'
                | 'RATING_DESC'
                | 'NEW_ARRIVAL_ASC'
                | 'NEW_ARRIVAL_DESC';

            searchPrefs?: TSearchPref[];
        };

        filters?: {
            applyFilters: {
                filters: [
                    {
                        /**
                         * The ID of the attribute to filter by, eg. color
                         */
                        key: string;
                        /**
                         * An array of values to filter by, eg. Red, Blue.
                         *
                         * When using range filters, specify the first value as the minimum value and the second as the max value.
                         * For example to retrieve records with prices between 60 to 80, use: "key": "klevu_price", "values": [60, 80].
                         *
                         * To retrieve steps of prices, for example those with values between 0-50 or 150-200,
                         * use: "key": "klevu_price", "values": ["0-50", "150-200"].
                         *
                         * To retrieve exact values, for example records with exact values 100 or 200,
                         * use: "key": "klevu_price", "values": ["100-100", "200-200"].
                         *
                         * By default all attributes submitted to Klevu are indexed as STRING attributes,
                         * which means they cannot be used as range filters. The product sale price field is the only exception
                         * to this rule, which is filtered using the key klevu_price. If you have explicitly requested and
                         * Klevu has approved that certain attributes be indexed as numerical attributes,
                         * you can also retrieve those as range filters.
                         */
                        values: string[];
                        settings: {
                            /**
                             * The behaviour when specifying multiple filters or values.
                             * See the notes below for more information on how to use this field.
                             */
                            singleSelect: boolean;
                        };
                    }
                ];
            };
        };
    }

    /**
     * Category sort request
     * https://developers.klevu.com/api/#category-navigation
     */
    export interface ISortRequest {
        context: IContext;
        recordQueries?: ISortRequestItem[];
    }

    /**
     * Category sort request
     */
    export interface ISortRequestItem {
        /**
         * 	Every suggestion request must have a unique identifier associated with it.
         *  When a result is retrieved, the ID of each query is associated with it's respective response.
         */
        id: string;
        /**
         * This parameter defines the type of request it is, which should be 'SEARCH' for search queries.
         * There are other values available, detailed elsewhere in the documentation.
         */
        typeOfRequest: 'CATNAV';

        settings: {
            limit?: number;
            fields?: string[];
            query: {
                /**
                 * Specify the name of the category to retrieve results from, in the same case and format as it is indexed with Klevu.
                 *
                 * For nested categories, use the ; character to separate the hierarchy.
                 * For example, for 'Mens > Shoes > Trainers and Sneakers' you would specify: Mens;Shoes;Trainers and Sneakers.
                 */
                categoryPath: string;
            };
        };
    }

    export type TRecord = 'KLEVU_PRODUCT' | 'KLEVU_CMS' | 'KLEVU_CATEGORY';

    /**
     * https://developers.klevu.com/api/#type-of-search
     */
    export type TSearchPref =
        | 'showOutOfStockProducts'
        | 'hideOutOfStockProducts'
        | 'disableStockSorting'
        | 'includeStopwords'
        | 'excludeIds'
        | 'includeDescription'
        | 'excludeDescription'
        | 'disableFuzzyMatch'
        | 'disableWildcard'
        | 'disableORSearch'
        | 'partialMatch'
        | 'partialMatchForAllWords'
        | 'disableWordShingles'
        | 'searchCompoundsAsAndQuery'
        | 'enableBoostingOriginalTermsInSynonyms'
        | 'showDisabledFacets'
        | 'showFiltersWithSmallCount'
        | 'includeCategoryFilterInCatNav'
        | 'enableScores'
        | 'debugQuery';

    /**
     * Search response
     * https://developers.klevu.com/api/#response-object
     */
    export interface ISearchResponse {
        meta: ISearchResponseMeta;
        suggestionResults: ISuggestionResponseItem[];
        queryResults: ISearchResponseItem[];
    }

    /**
     * Suggestion results
     */
    export interface ISuggestionResponseItem {
        id: string;
        suggestions: ISearchResponseSuggestion[];
    }

    export interface ISearchResponseSuggestion {
        /**
         * Suggested phrase
         */
        suggest: string;
    }

    /**
     * Search results
     */
    export interface ISearchResponseItem {
        /**
         * The ID of the record query for which the results are being returned.
         */
        id: string;
        /**
         * The meta object contains information on the query related parameters.
         */
        meta: {
            /**
             * API key
             */
            apiKey: string;
            /**
             * The time taken by the Klevu Search engine to fetch the response.
             */
            qTime: number;
            /**
             * The number of results requested to be returned for this query.
             */
            noOfResults: number;
            /**
             *  The total number of results found for this query.
             */
            totalResultsFound: number;
            /**
             * The index of the first result returned in this response.
             */
            offset: number;
            /**
             * The query type that was executed by Klevu to retrieve the results.
             */
            typeOfSearch: TQueryType;
            /**
             * Information that can be useful for debugging the query.
             * For example, the actual query that was fired by the Klevu Search engine,
             * inclusive of any synonyms or de-compounded words taken into consideration.
             */
            debuggingInformation: string;
            /**
             * The search term submitted for this query.
             */
            searchedTerm: string;
        };
        records: IResult[];
    }

    /**
     * The meta object contains information on the query related parameters.
     */
    export interface ISearchResponseMeta {
        /**
         * The number of results requested to be returned for this query.
         */
        noOfResults: number;
        paginationStartFrom: number;
        /**
         * The total number of results found for this query.
         */
        totalResultsFound: number;
        /**
         * This may be populated with a code if any actions were taken on the record. Possible values are:
         * 1: Nothing to report.
         * 2: The price of the record is using the base currency.
         */
        notificationCode: 1 | 2;
        /**
         * The time taken by the Klevu Search engine to fetch the response.
         */
        qTime: number;
        /**
         * The index of the first result returned in this response.
         */
        offset: number;
        /**
         * The query type that was executed by Klevu to retrieve the results.
         */
        typeOfSearch: TQueryType;
        /**
         * Information that can be useful for debugging the query.
         * For example, the actual query that was fired by the Klevu Search engine,
         * inclusive of any synonyms or de-compounded words taken into consideration.
         */
        debuggingInformation: string;
        /**
         * The search term submitted for this query.
         */
        searchedTerm: string;
    }

    /**
     * Error response
     */
    export interface IErrorResponse {
        errorCode: string;
        errorMessage: string;
    }

    export type TQueryType = 'WILDCARD_AND' | 'FUZZY_AND';

    export interface IResult {
        id?: string;
        name?: string;
        url?: string;
    }

    /**
     * Product result data
     */
    export interface IProduct extends IResult {
        itemGroupId: string;
        klevu_manual_boosting: string;
        image: string;
        shortDesc: string;
        sku: string;
        startPrice: string;
        storeBaseCurrency: string;
        currency: string;
        groupPrices: string;
        category: string;
        price: string;
        brand: string;
        salePrice: string;
        totalVariants: string;
        rating: string;
        inStock: 'yes' | 'no';
    }
    /**
     * Category result data
     */
    export interface ICategory {
        name: string;
        url: string;
        shortDesc: string;
    }

    export interface IPopularProduct {
        name: string;
        imageUrl: string;
        id: string;
    }

    export interface IPage {
        id: string;
        name: string;
        imageUrl: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Tracking
    ////////
    ////////////////////////////////////////////

    /**
     * Search tracking
     * https://www.klevu.com/developer-api/api/reporting.html#searched-terms
     */
    export interface IAnalyticsSearchedTermRequest {
        /**
         * This is your Klevu JS API Key.
         */
        klevu_apiKey: string;

        /**
         * This is the term being searched. For example “iphone 5s”.
         */
        klevu_term: string;

        /**
         * This is the total number of results returned for the searched term. It must be an integer (e.g. 5)
         */
        klevu_totalResults: number;
        /**
         * IP address of the shopper who searched a term. If calling from javascript (i.e. an ajax call), this is an optional parameter.
         */
        klevu_shopperIP?: string;
        /**
         * When you receive a response for a search query, look at the value of the “typeOfQuery” element under the “metaData” s
         * ection (e.g. WILDCARD_AND, FUZZY_AND). You need to pass this value to this parameter.
         */
        klevu_typeOfQuery: TQueryType;
    }

    export interface ITrackSearchProductClickData {
        product: Product<IProductDataV2>;
        term?: string;
    }

    /**
     * Product click tracking
     * https://www.klevu.com/developer-api/api/reporting.html#clicked-records
     */
    export interface IAnalyticsClickedProductRequest {
        /**
         * This is your Klevu JS API Key.
         */
        klevu_apiKey: string;

        /**
         * This is the term being searched. For example “iphone 5s”.
         */
        klevu_keywords: string;

        /**
         * Here, the value must be “clicked”.
         */
        klevu_type: 'clicked';

        /**
         * This is the product code of the clicked product
         */
        klevu_productId: string;
        /**
         * This is the parent ID of the clicked product. eg. 54321.
         * For compound products with a parent and multiple child/variant products,
         * this is the common ID which ties the products together.
         * For simple products, please specify the same as klevu_productId.
         */
        klevu_productGroupId: string;
        /**
         * This is the child/variant ID of the clicked product. eg. 12345.
         * For compound products with a parent and multiple child/variant products,
         * this is the ID of the specific variant.
         * For simple products, please specify the same as klevu_productId.
         */
        klevu_productVariantId: string;
        /**
         * This is the product name of the clicked product.
         */
        klevu_productName: string;
        /**
         * This is the product URL of the clicked product.
         */
        klevu_productUrl: string;

        /**
         * IP address of the shopper who clicked on a product.
         * If calling from javascript (i.e. an ajax call), this is an optional parameter.
         */
        klevu_shopperIP?: string;
    }

    /**
     * Product transaction tracking
     * https://www.klevu.com/developer-api/api/reporting.html#checked-out-products
     */
    export interface IAnalyticsSoldProductRequest {
        /**
         * This is your Klevu JS API Key.
         */
        klevu_apiKey: string;

        /**
         * Here, the value must be “clicked”.
         */
        klevu_type: 'checkout';

        /**
         * This is the product code of the clicked product
         */
        klevu_productId: string;
        /**
         * This is the parent ID of the clicked product. eg. 54321.
         * For compound products with a parent and multiple child/variant products,
         * this is the common ID which ties the products together.
         * For simple products, please specify the same as klevu_productId.
         */
        klevu_productGroupId: string;
        /**
         * This is the child/variant ID of the clicked product. eg. 12345.
         * For compound products with a parent and multiple child/variant products,
         * this is the ID of the specific variant.
         * For simple products, please specify the same as klevu_productId.
         */
        klevu_productVariantId: string;
        /**
         * This is the total quantity purchased. It must be an integer (e.g. 5).
         */
        klevu_unit: number;
        /**
         * Product’s sale price for one unit. This must be a double Value.
         */
        klevu_salePrice: number;
        /**
         * e.g GBP, USD, EUR.
         */
        klevu_currency: string;

        /**
         * IP address of the shopper who clicked on a product.
         * If calling from javascript (i.e. an ajax call), this is an optional parameter.
         */
        klevu_shopperIP?: string;
    }

    export interface IAnalyticsCategoryPageViewRequest {
        klevu_apiKey: string;
        klevu_categoryName: string;
        klevu_categoryPath: string;
        klevu_productIds: string;
        klevu_pageStartsFrom?: string;
        klevu_shopperIP?: string;
    }

    export interface ITrackCategoryProductClickData {
        product: Product<IProductDataV2>;
        category: Category<ICategoryDataV3>;
        position?: number;
        searchType?: string;
    }

    export interface IAnalyticsCategoryProductClickRequest {
        klevu_apiKey: string;
        klevu_categoryName: string;
        klevu_categoryPath: string;
        klevu_productId: string;
        klevu_productGroupId: string;
        klevu_productVariantId: string;
        klevu_productName?: string;
        klevu_productUrl?: string;
        klevu_productSku?: string;
        klevu_salePrice?: string;
        klevu_productRatings?: number;
        klevu_productPosition?: number;
        klevu_shopperIP?: string;
        klevu_abTestId?: string;
        klevu_abTestVariantId?: string;
    }

    export enum EAnalyticsRecommendationEvent {
        view = 'view_recs_list',
        select = 'select_recs_list'
    }

    export interface IAnalyticsRecommendationTrackRequest<T extends EAnalyticsRecommendationEvent> {
        event: T;
        event_apikey: string;
        event_list_id: string;
        event_list_name: string;
        event_list_logic: string;
        event_tags?: string[];
        items: IAnalyticsRecommendationProductItem[];
    }

    export interface IAnalyticsRecommendationProductsViewRequest
        extends IAnalyticsRecommendationTrackRequest<EAnalyticsRecommendationEvent.view> {}

    export interface IAnalyticsRecommendationProductItem {
        item_id: string;
        item_group_id: string;
        item_variant_id: string;
        item_name: string;
        price: number;
        currency?: string;
        item_brand?: string;
        item_category?: string;
        index: number;
    }

    export interface IAnalyticsRecommendationProductClickRequest
        extends IAnalyticsRecommendationTrackRequest<EAnalyticsRecommendationEvent.select> {}

    export interface IAnalyticsRecommendationTrackData<T extends EAnalyticsRecommendationEvent> {
        eventListId: string;
        event: T;
        products: {
            model: Product<IProductDataV2>;
            position: number;
        }[];
    }

    export interface IKeywordUrlMap {
        keywords: string[];
        url: string;
    }

    export interface InoResultMessage {
        noResultsOptions: INoResultsOptions;
    }
    export interface INoResultsOptions {
        messages: INoResultsMessage[];
    }
    export interface INoResultsMessage {
        message: string;
        showForTerms: string[];
    }

    export interface IBanner {
        bannerAltTag?: string;
        bannerImg?: string;
        bannerName?: string;
        bannerRef?: number;
        endDate?: string;
        position?: string;
        redirectUrl?: string;
        showForTerms?: string[];
        showOnCategoryPage?: boolean;
        showOnLandingPage?: boolean;
        showOnQuickSearch?: boolean;
        startDate?: string;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Recommendations
    ////////
    ////////////////////////////////////////////

    export const recommendationsEngineId = 'klevuRecommendations';
    export const contextRecommendationsEngineId = 'klevuContextRecommendations';
    export const contextRecommendationsBasketEngineId = 'klevuContextRecommendationsBasket';

    export function isKlevuEngine(engines: string | (string | IEngineRequest)[]) {
        const test = [recommendationsEngineId, contextRecommendationsEngineId];
        if (typeof engines === 'string') {
            return test.includes(engines);
        }
        if (Array.isArray(engines)) {
            return !!engines.find(engine => test.includes(typeof engine === 'string' ? engine : engine.id));
        }
        return false;
    }

    export interface IRecommendationsOptions {
        klevuTypeId: ERecommendationTypeId;
        klevuProductsIds: number[];
        klevuCategoryId: number;
        klevuEnablePersonalisation?: boolean;
        klevuLimit: number;
        klevuRecommendationId?: string;
        useRecommendationId?: boolean;
    }

    export interface IContextRecommendationsOptions extends KlevuSearch.IRecommendationsOptions {
        strategyId?: string;
        isRecentlyViewed?: boolean;
    }

    export enum ERecommendationTypeId {
        NewArrivals = 'recsNewArrivals',
        SimilarProducts = 'recsSimilar',
        TrendingProducts = 'recsTrending',
        CustomersAlsoViewed = 'recsAlsoViewed',
        CustomersAlsoBought = 'recsAlsoBought',
        RecentlyViewed = 'recsRecentlyViewed',
        VisuallySimilar = 'klevuRECSItemList',
        HandPicked = 'recsHandPicked'
    }

    export enum ERecommendationLogic {
        NewArrivals = 'NEWEST_ARRIVALS',
        Similar = 'SIMILAR',
        Trending = 'TRENDING',
        TrendingPersonalized = 'TRENDING_PERSONALIZED',
        CustomersAlsoViewed = 'OTHER_ALSO_VIEWED',
        CustomersAlsoBought = 'BOUGHT_TOGETHER',
        RecentlyViewed = 'RECENTLY_VIEWED',
        HandPicked = 'HAND_PICKED'
    }

    export interface IRecommendationRequest {
        context: IContext;
        recordQueries?: IRecommendationRequestItem[];
    }

    export interface IRecommendationRequestItem {
        id: ERecommendationTypeId;
        typeOfRequest: ETypeOfRequest;
        settings: IRecommendationRequestSettings;
    }

    export type IRecommendationRequestSettings =
        | IRecommendationRecentObjectSettings
        | IRecommendatioinVisuallySimilarSettings
        | IRecommendationNewArrivalsSettings
        | IRecommendationTrendingProductsSettings
        | IRecommendationCustomAndQuerySettings;

    export interface IRecommendationSettings {
        typeOfRecords: [ETypeOfRecord.Product];
    }

    export interface IRecommendationRecentObjectSettings extends IRecommendationSettings {
        context: IRecentObjectContext;
    }

    export interface IRecommendatioinVisuallySimilarSettings extends IRecommendationSettings {
        context: ISourceObjectsContext;
        limit: number;
    }

    export interface IRecentObjectContext {
        recentObjects: {
            typeOfRecord: ETypeOfRecord.Product;
            records: { id: string }[];
        }[];
    }

    interface ISourceObjectsContext {
        sourceObjects: {
            typeOfRecord: ETypeOfRecord.Product;
            records: ({ id: string } | { itemGroupId: string })[];
        }[];
    }

    export interface IRecommendationNewArrivalsSettings extends IRecommendationSettings {
        query: {
            // Optional. This is only required when you want to give Category-specific context to a recommendation query.
            // Specify the name of the category to retrieve results from, in the same case and format as it is indexed with Klevu.
            // For nested categories, use the ; character to separate the hierarchy.
            // For example, for 'Mens > Shoes > Trainers and Sneakers' you would specify: Mens;Shoes;Trainers and Sneakers.
            categoryPath?: string;
        };
    }

    export interface IRecommendationTrendingProductsSettings extends IRecommendationRecentObjectSettings {
        query: {
            // Optional. This is only required when you want to give Category-specific context to a recommendation query.
            // Specify the name of the category to retrieve results from, in the same case and format as it is indexed with Klevu.
            // For nested categories, use the ; character to separate the hierarchy.
            // For example, for 'Mens > Shoes > Trainers and Sneakers' you would specify: Mens;Shoes;Trainers and Sneakers.
            categoryPath?: string;
        };
        personalisation: {
            enablePersonalisation?: boolean;
        };
    }

    export interface IRecommendationCustomAndQuerySettings extends IRecommendationSettings {
        // Here we make a custom query to Klevu, since we know exactly which records we expect in the response.
        // Specify the IDs of all of the products your customer has recently viewed, separated by OR.
        // "id:(\"34322453430403\" OR \"34322423840899\")"
        customANDQuery: string;
        // Use this field to specify the order the results should be returned, with the most recently viewed product coming first.
        topIds: { key: 'id'; value: string }[];
    }

    /**
     * Interface for modal carousel
     */
    export interface IRecommendationsModelCarouselLoadRequest extends KlevuSearch.IRecommendationsOptions {
        variant: 'string';
        limit: number;
        ids: number[];
        category_id: number;
        verbosity?: number;
        isRecentlyViewed?: boolean;
    }

    /**
     * Interface for modal carousel
     */
    export interface IContextRecommendationsModelCarouselLoadRequest extends IRecommendationsModelCarouselLoadRequest {
        strategyId?: string;
    }

    /**
     * Interface for releated products
     */
    export interface IRecommendationsRelatedProductsOptions extends KlevuSearch.IRecommendationsOptions {
        engine: string;
        limit: number;
    }

    export interface IRecommendationsConfiguration {
        metadata: IRecommendationsConfigurationMetadata;
        scripts: {
            recsObject: string;
        };
        search: IRecommendationsConfigurationSearch;
        styles: { base: string };
        templates: { base: string };
    }

    export interface IRecommendationsConfigurationMetadata {
        enabled: boolean;
        logic: ERecommendationLogic;
        maxProducts?: number;
        pageType: string;
        productThreshold?: number;
        recsKey: string;
        title: string;
    }

    export interface IRecommendationsConfigurationSearch {
        basePath: string;
        payload: string;
    }

    /**
     * AB TESTING
     */
    export interface IABStorage {
        expires: number;
        data: IABEntry[] | undefined;
    }

    export interface IABResponse {
        assigned: IABEntry[];
    }

    export interface IABEntry {
        sourceId: string; // 'new;horse',
        abTestId: string; // '6391dfcc645686561620712d',
        abTestVariantId: string; // 'c9f4bc1e-9ec5-4c77-9f64-34ab0c927f98',
        type: 'CAT_NAV';
    }
}
